/**
 * Scenario translation between UWM Easy Qualifier inputs (layout body keys + option
 * ids, e.g. occupancyTypeId "2") and the lender-sheet field catalog (PRICING_FIELDS
 * display values, e.g. occupancy "Investment Property").
 *
 * Moved here unchanged from apps/api/src/services/scenario-bridge.ts so the Mana Pricing
 * page can keep UWM's request and the rate-sheet scenario in step in the browser; the
 * API re-exports these for the Pricing and Other Lenders modules.
 *
 * Ids come from UWM's Instant Price Quote guide (Docs/uwm-price-quote-guide.txt) and
 * the live mortgage layout; when a layout is supplied, option ids are re-resolved from
 * their labels so a renumbered option still maps.
 */
import { amortizationsOf, fieldValueList, loanTypesOf, parseBuydownOption } from '../lender-pricing-fields.js';

/** One field of UWM's *-pricing-layout response. */
export interface UwmLayoutField {
  itemId: string;
  label: string;
  defaultValue: string | null;
  isDisplay: boolean;
  isRequired: boolean;
  displayInputType: string;
  selectListData: Array<{ id: string; displayValue: string; fieldValueDescription?: string }> | null;
}

export type BridgeSource = 'pricing' | 'other_lenders';

export interface BridgeResult {
  inputs: Record<string, unknown>;
  /** Things that could not be carried across (shown to the MLO after loading). */
  notes: string[];
}

// --- UWM option ids (mortgage layout) ----------------------------------------
export const UWM_IDS = {
  loanType: { conventional: '0', conventionalArm: '1', fha: '2', fhaArm: '3', va: '4', vaArm: '6', usda: '5' },
  purpose: { purchase: '1', refinance: '3' },
  occupancy: { primary: '1', investment: '2', second: '3' },
  property: { sfr: '22', units: '25', coop: '30', pud: '32', condo: '34', modular: '38', siteCondo: '48', mhSingle: '54', mhMulti: '55', mhPudSingle: '53', mhCondoSingle: '52', mhPudMulti: '57', mhCondoMulti: '56' },
  attachment: { attached: '1', detached: '0' },
  term: { 30: '4', 25: '3', 20: '2', 15: '1', 10: '0' } as Record<number, string>,
  lock: { 15: '12', 30: '13', 45: '14', 60: '15', 90: '16' } as Record<number, string>,
  escrow: { none: '1', taxes: '2', hazard: '3', both: '4' },
  prepay: { none: '1', y1: '7', y2: '2', y3: '4' },
  vaUse: { first: '8', subsequent: '9' },
  fundingFee: { financed: '6', nonFinanced: '7', exempt: '12' },
  refi: { cashOut: 'Cash Out / Other', rtConv: 'Rate And Term Reduction-CONV', rtFha: 'Rate And Term Reduction-FHA', streamline: 'Streamline Refinance', irrrl: 'VA Interest Rate Reduction', vaCashOut: 'VA Cash Out - Type II' },
};
const UWM = UWM_IDS;

/**
 * Monthly income sent to UWM when the scenario has none. UWM rejects a zero income and is the
 * only lender that needs one (the rate sheets don't price on income) — decided 2026-09-13.
 */
export const UWM_DEFAULT_MONTHLY_INCOME = 20000;

/** Option id for a label pattern on a layout field, else the documented default id. */
function optionId(layout: UwmLayoutField[] | null, itemId: string, label: RegExp, fallback: string): string {
  const f = layout?.find((x) => x.itemId === itemId);
  const hit = f?.selectListData?.find((o) => label.test(o.displayValue));
  return hit?.id ?? fallback;
}
/** Display label of an option id, else null. */
function optionLabel(layout: UwmLayoutField[] | null, itemId: string, id: string): string | null {
  const f = layout?.find((x) => x.itemId === itemId);
  return f?.selectListData?.find((o) => o.id === id)?.displayValue ?? null;
}

const str = (v: unknown): string => (v == null ? '' : String(v).trim());

/** Other Lenders scenarios the Pricing (mortgage) module can load: first-lien, not HELOC. */
export function isLenderScenarioBridgeable(inputs: Record<string, unknown>): boolean {
  return !loanTypesOf(inputs).some((t) => /heloc/i.test(t)) && !/second/i.test(str(inputs.lienPosition));
}

// ---------------------------------------------------------------------------
// Loan types on UWM: each id fuses the loan type with its amortization
// (0 Conventional, 1 Conventional ARM, 2 FHA, 3 FHA ARM, 4 VA, 6 VA ARM, 5 USDA).
// ---------------------------------------------------------------------------
export type UwmLoanFamily = 'conventional' | 'fha' | 'va' | 'usda';
export const UWM_FAMILY_LABEL: Record<UwmLoanFamily, string> = { conventional: 'Conventional', fha: 'FHA', va: 'VA', usda: 'USDA' };
type UwmLoanTypeKey = keyof typeof UWM_IDS.loanType;
const UWM_LOAN_TYPE_KEY: Record<UwmLoanFamily, { Fixed: UwmLoanTypeKey; ARM: UwmLoanTypeKey | null }> = {
  conventional: { Fixed: 'conventional', ARM: 'conventionalArm' },
  fha: { Fixed: 'fha', ARM: 'fhaArm' },
  va: { Fixed: 'va', ARM: 'vaArm' },
  usda: { Fixed: 'usda', ARM: null },
};
const UWM_LOAN_TYPE_LABEL: Record<UwmLoanTypeKey, RegExp> = {
  conventional: /^Conventional$/i,
  conventionalArm: /^Conventional ARM/i,
  fha: /^FHA$/i,
  fhaArm: /^FHA ARM/i,
  va: /^VA$/i,
  vaArm: /^VA ARM/i,
  usda: /^USDA/i,
};
const UWM_LOAN_TYPE_NAMES: Record<string, string> = { '0': 'Conventional', '1': 'Conventional ARM', '2': 'FHA', '3': 'FHA ARM', '4': 'VA', '6': 'VA ARM', '5': 'USDA' };

/** The UWM loan type family of a Loan Type pick (Jumbo / Non-QM price as Conventional; DSCR and HELOC have none). */
function uwmFamilyOfPick(loanType: string): UwmLoanFamily | null {
  if (/^(dscr|heloc)$/i.test(loanType)) return null;
  return /^fha/i.test(loanType) ? 'fha' : /^va$/i.test(loanType) ? 'va' : /^usda/i.test(loanType) ? 'usda' : 'conventional';
}

/** Which family a UWM loan type id belongs to (by its layout label, else the documented id). */
export function uwmFamilyOfLoanTypeId(id: string, layout: UwmLayoutField[] | null): UwmLoanFamily {
  const label = optionLabel(layout, 'LoanTypeIds', id) ?? UWM_LOAN_TYPE_NAMES[id] ?? '';
  return /fha/i.test(label) ? 'fha' : /\bva\b/i.test(label) ? 'va' : /usda/i.test(label) ? 'usda' : 'conventional';
}

/**
 * The UWM loan type ids a scenario asks for — every picked loan type UWM offers × every picked
 * amortization — and their families in pick order. Empty families when only DSCR / HELOC is picked.
 */
export function uwmLoanTypePlan(inputs: Record<string, unknown>, layout: UwmLayoutField[] | null): { ids: string[]; families: UwmLoanFamily[]; notes: string[] } {
  const notes: string[] = [];
  const families: UwmLoanFamily[] = [];
  const picks = loanTypesOf(inputs);
  for (const lt of picks) {
    const fam = uwmFamilyOfPick(lt);
    if (!fam) {
      if (/^dscr$/i.test(lt) && picks.length > 1) notes.push('DSCR is priced on the rate sheets only — UWM has no DSCR product.');
      continue;
    }
    if (fam === 'conventional' && !/^conventional$/i.test(lt)) notes.push(`Loan type "${lt}" priced as Conventional on UWM (it has no separate ${lt} loan type).`);
    if (!families.includes(fam)) families.push(fam);
  }
  const ids: string[] = [];
  for (const fam of families) {
    for (const am of amortizationsOf(inputs)) {
      const key = UWM_LOAN_TYPE_KEY[fam][am];
      if (!key) {
        notes.push(`${UWM_FAMILY_LABEL[fam]} has no ARM on UWM — ${amortizationsOf(inputs).includes('Fixed') ? 'only its fixed rate is priced' : 'pick Fixed to price it'}.`);
        continue;
      }
      ids.push(optionId(layout, 'LoanTypeIds', UWM_LOAN_TYPE_LABEL[key], UWM_IDS.loanType[key]));
    }
  }
  return { ids: [...new Set(ids)], families, notes };
}

/** What a refinance is for, read off the Loan Purpose pick. */
export type UwmRefiIntent = 'rate_term' | 'cash_out' | 'streamline' | 'irrrl';
export function uwmRefiIntentOf(loanPurpose: unknown): UwmRefiIntent {
  const p = str(loanPurpose);
  return /cash/i.test(p) ? 'cash_out' : /irrrl/i.test(p) ? 'irrrl' : /streamline/i.test(p) ? 'streamline' : 'rate_term';
}

/** The same intent read back off a UWM refinance purpose (a saved request carries the first loan type's). */
export function uwmRefiIntentOfPurposeId(refinancePurposeId: unknown, layout: UwmLayoutField[] | null): UwmRefiIntent {
  const id = str(Array.isArray(refinancePurposeId) ? refinancePurposeId[0] : refinancePurposeId);
  const label = `${optionLabel(layout, 'RefinancePurposeID', id) ?? ''} ${id}`;
  return /cash/i.test(label) ? 'cash_out' : /streamline/i.test(label) ? 'streamline' : /irrrl|interest rate reduction/i.test(label) ? 'irrrl' : 'rate_term';
}

/**
 * The refinance purpose each UWM loan type takes for an intent (UWM's guide): Conventional —
 * cash-out or rate/term; FHA — rate/term, cash-out or streamline; VA — IRRRL or cash-out
 * (type II); USDA — rate/term only. An intent a loan type doesn't offer takes its rate/term
 * equivalent (VA's is the IRRRL).
 */
export function uwmRefinancePurposeFor(family: UwmLoanFamily, intent: UwmRefiIntent, layout: UwmLayoutField[] | null): string {
  const cashOut = () => optionId(layout, 'RefinancePurposeID', /^Cash Out$/i, UWM_IDS.refi.cashOut);
  const rtConv = () => optionId(layout, 'RefinancePurposeID', /Rate and term - Conv$/i, UWM_IDS.refi.rtConv);
  if (family === 'va') return intent === 'cash_out' ? optionId(layout, 'RefinancePurposeID', /VA cash-out - type II/i, UWM_IDS.refi.vaCashOut) : optionId(layout, 'RefinancePurposeID', /IRRRL/i, UWM_IDS.refi.irrrl);
  if (family === 'fha') {
    if (intent === 'cash_out') return cashOut();
    if (intent === 'streamline') return optionId(layout, 'RefinancePurposeID', /^Streamline$/i, UWM_IDS.refi.streamline);
    return optionId(layout, 'RefinancePurposeID', /Rate and term - FHA/i, UWM_IDS.refi.rtFha);
  }
  if (family === 'usda') return rtConv();
  return intent === 'cash_out' ? cashOut() : rtConv();
}
const money = (v: unknown): number => {
  const n = Number(str(v).replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : 0;
};
const truthy = (v: unknown): boolean => v === true || /^(true|yes)$/i.test(str(v));

// ---------------------------------------------------------------------------
// Other Lenders Pricing → Pricing (UWM body keys)
// ---------------------------------------------------------------------------
export function lenderInputsToUwm(inputs: Record<string, unknown>, layout: UwmLayoutField[] | null): BridgeResult {
  const notes: string[] = [];
  const out: Record<string, unknown> = {};
  const purposeRaw = str(inputs.loanPurpose);
  const isPurchase = /purchase/i.test(purposeRaw) || !purposeRaw;

  // Loan types (+ ARM variants): every picked loan type UWM offers × every picked amortization.
  // A refinance takes one loan type per UWM request, so the quote splits it (priceUwm); a
  // purchase sends them together, as UWM allows.
  const plan = uwmLoanTypePlan(inputs, layout);
  notes.push(...plan.notes);
  const families: UwmLoanFamily[] = plan.families.length ? plan.families : ['conventional'];
  out.loanTypeIds = plan.ids.length
    ? plan.ids
    : amortizationsOf(inputs).map((am) => optionId(layout, 'LoanTypeIds', am === 'ARM' ? /^Conventional ARM/i : /^Conventional$/i, am === 'ARM' ? UWM.loanType.conventionalArm : UWM.loanType.conventional));

  // Amounts
  const loanAmount = money(inputs.baseLoanAmount);
  const value = money(inputs.propertyValue);
  out.loanAmount = loanAmount;
  out.appraisedValue = value;
  out.salesPrice = isPurchase ? value : 0;
  out.loanToValue = value > 0 ? Math.round((loanAmount / value) * 100000) / 1000 : 0;
  if (truthy(inputs.subordinateFinancing)) notes.push('Subordinate financing: enter the second loan amount on UWM (not carried across).');

  // Purpose
  out.purposeTypeId = optionId(layout, 'PurposeTypeId', isPurchase ? /^Purchase/i : /^Refinance 1st/i, isPurchase ? UWM.purpose.purchase : UWM.purpose.refinance);
  if (!isPurchase) {
    // The request carries the first loan type's purpose; a split refinance gives each its own.
    const intent = uwmRefiIntentOf(purposeRaw);
    out.refinancePurposeID = uwmRefinancePurposeFor(families[0]!, intent, layout);
    if (intent === 'streamline' && families.some((f) => f === 'conventional')) notes.push('Conventional has no streamline refinance on UWM — its rate/term refinance is priced instead.');
    if (intent === 'cash_out' && families.includes('usda')) notes.push('USDA offers only a rate/term refinance on UWM — that is what it prices.');
  }

  // Occupancy
  const occ = str(inputs.occupancy);
  out.occupancyTypeId = /invest/i.test(occ)
    ? optionId(layout, 'OccupancyTypeId', /Investment/i, UWM.occupancy.investment)
    : /second/i.test(occ)
      ? optionId(layout, 'OccupancyTypeId', /Second/i, UWM.occupancy.second)
      : optionId(layout, 'OccupancyTypeId', /Primary/i, UWM.occupancy.primary);

  // Property type
  const structure = str(inputs.legalStructure);
  const attached = /attached/i.test(str(inputs.attachmentType)) && !/detached/i.test(str(inputs.attachmentType));
  const units = Number((str(inputs.numberOfUnits).match(/^(\d)/) ?? [])[1] ?? 1);
  const mh = str(inputs.manufactured);
  let propKey: keyof typeof UWM.property = 'sfr';
  let propLabel = /^Single family/i;
  if (mh && !/^no$/i.test(mh)) {
    const single = /single/i.test(mh);
    if (/condo/i.test(structure)) [propKey, propLabel] = single ? ['mhCondoSingle', /Manufactured condo single/i] : ['mhCondoMulti', /Manufactured condo multi/i];
    else if (/pud/i.test(structure)) [propKey, propLabel] = single ? ['mhPudSingle', /Manufactured PUD single/i] : ['mhPudMulti', /Manufactured PUD multi/i];
    else [propKey, propLabel] = single ? ['mhSingle', /Manufactured single wide/i] : ['mhMulti', /Manufactured multi wide/i];
    if (/advantage|choice/i.test(mh)) notes.push('MH Advantage / CHOICEHome is not a UWM input — priced as a standard manufactured home.');
  } else if (units >= 2) [propKey, propLabel] = ['units', /2-4 unit/i];
  else if (/condo/i.test(structure)) [propKey, propLabel] = attached ? ['condo', /^Condominium/i] : ['siteCondo', /Site condo/i];
  else if (/pud/i.test(structure)) [propKey, propLabel] = ['pud', /Planned unit/i];
  out.propertyTypeID = optionId(layout, 'PropertyTypeID', propLabel, UWM.property[propKey]);
  out.numberOfUnits = units >= 2 ? units : 1;
  out.attachmentTypeID = attached ? optionId(layout, 'AttachmentTypeID', /^Attached/i, UWM.attachment.attached) : optionId(layout, 'AttachmentTypeID', /^Detached/i, UWM.attachment.detached);

  // Location + borrower
  out.propertyZipCode = str(inputs.propertyZip);
  const fico = Number(str(inputs.creditScore));
  if (Number.isFinite(fico) && fico > 0) out.creditScore = fico;
  const income = money(inputs.monthlyQualifyingIncome);
  out.monthlyIncome = income > 0 ? income : UWM_DEFAULT_MONTHLY_INCOME;
  const dti = Number(str(inputs.dtiRatio).replace('%', ''));
  if (Number.isFinite(dti) && dti > 0) out.isDTIOver45Percent = dti > 45;
  out.firstTimeHomeBuyer = truthy(inputs.firstTimeHomeBuyer) || truthy(inputs.fthbAmiWaiver);
  if (!truthy(inputs.firstTimeHomeBuyer) && truthy(inputs.fthbAmiWaiver)) notes.push('"First-time buyer within AMI limits" was carried across as UWM\'s First-time Homebuyer = Yes.');

  // Terms (multi-select on both sides), lock, escrows
  const termIds: string[] = [];
  const termLabels = fieldValueList(inputs.term);
  for (const raw of termLabels.length ? termLabels : ['30 Years']) {
    const termYears = Number((raw.match(/(\d+)/) ?? [])[1] ?? 30);
    const termId = UWM.term[termYears];
    if (termId) termIds.push(optionId(layout, 'LoanTermIds', new RegExp(`^${termYears} years`, 'i'), termId));
    else notes.push(`${termYears}-year term is not offered on UWM — dropped.`);
  }
  if (termIds.length) out.loanTermIds = [...new Set(termIds)];
  else {
    out.loanTermIds = [optionId(layout, 'LoanTermIds', /^30 years/i, UWM.term[30]!)];
    notes.push('No UWM-supported term was selected — loaded as 30 years.');
  }
  const lockReq = Number(str(inputs.desiredLockPeriod)) || 30;
  const lockDays = [15, 30, 45, 60, 90].find((d) => d >= lockReq) ?? 90;
  if (lockDays !== lockReq) notes.push(`${lockReq}-day lock is not a UWM lock period — loaded as ${lockDays} days.`);
  out.commitmentPeriodID = optionId(layout, 'CommitmentPeriodID', new RegExp(`^${lockDays}-day`, 'i'), UWM.lock[lockDays]!);
  out.escrowWaiverTypeId = truthy(inputs.waiveEscrows) === true || /yes/i.test(str(inputs.waiveEscrows))
    ? optionId(layout, 'EscrowWaiverTypeId', /taxes & hazard/i, UWM.escrow.both)
    : optionId(layout, 'EscrowWaiverTypeId', /^No waiver/i, UWM.escrow.none);

  // Broker comp basis
  out.compensationPayerTypeID = /^bp/i.test(str(inputs.compType))
    ? optionId(layout, 'CompensationPayerTypeID', /Borrower paid/i, '0')
    : optionId(layout, 'CompensationPayerTypeID', /Lender paid/i, '1');

  // Buydown: UWM takes one BuyDownAliasID; the Pricing module compares the BP/LLPA
  // programs in its results, so only the first selected buydown carries across.
  const buydowns = fieldValueList(inputs.buydown).map(parseBuydownOption).filter((b): b is NonNullable<typeof b> => !!b);
  const bd = buydowns[0];
  if (bd) {
    const opt = optionId(layout, 'BuyDownAliasID', new RegExp(`^${bd.base.replace(/-/g, '\\s*-\\s*')}$`, 'i'), '');
    if (opt) out.buyDownAliasID = opt;
    else notes.push(`Buydown ${bd.label} is not offered on UWM — loaded without a buydown.`);
    if (buydowns.length > 1) notes.push(`Only the first buydown (${bd.label}) was carried to UWM — compare the other programs with "Compare programs" in the results.`);
  }

  // VA extras + prepayment penalty (investment)
  if (families.includes('va')) {
    out.useMortgageInsuranceTypeIds = [truthy(inputs.vaSubsequentUse) ? optionId(layout, 'useMortgageInsuranceTypeIds', /Subsequent/i, UWM.vaUse.subsequent) : optionId(layout, 'useMortgageInsuranceTypeIds', /First use/i, UWM.vaUse.first)];
    if (truthy(inputs.vaExemptFundingFee)) out.financedMortgageInsuranceTypeId = optionId(layout, 'FinancedMortgageInsuranceTypeId', /Exempt/i, UWM.fundingFee.exempt);
  }
  const ppp = str(inputs.prepaymentPenalty);
  if (ppp && !/none/i.test(ppp)) {
    const yrs = Number((ppp.match(/(\d)/) ?? [])[1] ?? 0);
    const id = yrs === 1 ? UWM.prepay.y1 : yrs === 2 ? UWM.prepay.y2 : yrs >= 3 ? UWM.prepay.y3 : UWM.prepay.none;
    out.prePaymentPenaltyId = optionId(layout, 'PrePaymentPenaltyId', new RegExp(`^${Math.min(yrs, 3)} Year`, 'i'), id);
    if (yrs > 3) notes.push(`${ppp} prepayment penalty: UWM offers up to 3 years — loaded as 3 years.`);
  }

  const program = str(inputs.productType);
  if (program && !/standard/i.test(program)) notes.push(`Program "${program}" is not a UWM input — its products appear in the quote results instead.`);
  if (str(inputs.desiredPrice) && str(inputs.desiredPrice) !== '100.000') notes.push('Desired price is not a UWM input — use the Target Price field on UWM if needed.');
  return { inputs: out, notes };
}

// ---------------------------------------------------------------------------
// Home equity → UWM's HELOC request (heloc-pricing-layout body keys)
// ---------------------------------------------------------------------------
/**
 * UWM's HELOC layout fields: CreditScore, PropertyZipCode, FirstLienAmount, AppraisedValue,
 * TotalLineAmount, InitialDrawAmount, OccupancyTypeId, LoanTermIds, RefinancePurposeID,
 * CommitmentPeriodID, PropertyTypeID, NumberOfUnits, AttachmentTypeID, HomeEquityTypeId
 * (1 = HELOC, 2 = HELOAN). Everything else (loan officer, borrower name) stays UWM-only.
 */
export function lenderInputsToUwmHeloc(inputs: Record<string, unknown>, layout: UwmLayoutField[] | null): BridgeResult {
  const notes: string[] = [];
  const out: Record<string, unknown> = {};
  const loanAmount = money(inputs.baseLoanAmount);
  const value = money(inputs.propertyValue);
  const draw = money(inputs.initialDraw);
  const senior = /second/i.test(str(inputs.lienPosition)) ? money(inputs.seniorLienBalance) : 0;
  out.totalLineAmount = loanAmount;
  out.initialDrawAmount = draw > 0 ? Math.min(draw, loanAmount) : loanAmount;
  out.appraisedValue = value;
  out.firstLienAmount = senior;
  const fico = Number(str(inputs.creditScore));
  if (Number.isFinite(fico) && fico > 0) out.creditScore = fico;
  out.propertyZipCode = str(inputs.propertyZip);

  const occ = str(inputs.occupancy);
  out.occupancyTypeId = /invest/i.test(occ)
    ? optionId(layout, 'OccupancyTypeId', /Investment/i, UWM.occupancy.investment)
    : /second/i.test(occ)
      ? optionId(layout, 'OccupancyTypeId', /Second/i, UWM.occupancy.second)
      : optionId(layout, 'OccupancyTypeId', /Primary/i, UWM.occupancy.primary);

  const structure = str(inputs.legalStructure);
  const attached = /attached/i.test(str(inputs.attachmentType)) && !/detached/i.test(str(inputs.attachmentType));
  const units = Number((str(inputs.numberOfUnits).match(/^(\d)/) ?? [])[1] ?? 1);
  let propKey: keyof typeof UWM.property = 'sfr';
  let propLabel = /^Single family/i;
  if (units >= 2) [propKey, propLabel] = ['units', /2-4 unit/i];
  else if (/condo/i.test(structure)) [propKey, propLabel] = attached ? ['condo', /^Condominium/i] : ['siteCondo', /Site condo/i];
  else if (/pud/i.test(structure)) [propKey, propLabel] = ['pud', /Planned unit/i];
  if (/manufactured|single-wide|double-wide/i.test(str(inputs.manufactured)) && !/^no$/i.test(str(inputs.manufactured))) notes.push('Manufactured homes are not a UWM HELOC property type — sent as a single family residence.');
  out.propertyTypeID = optionId(layout, 'PropertyTypeID', propLabel, UWM.property[propKey]);
  out.numberOfUnits = units >= 2 ? units : 1;
  out.attachmentTypeID = attached ? optionId(layout, 'AttachmentTypeID', /^Attached/i, UWM.attachment.attached) : optionId(layout, 'AttachmentTypeID', /^Detached/i, UWM.attachment.detached);

  // Purpose: UWM's HELOC takes a refinance purpose (cash-out for a standalone / piggyback second).
  const purposeRaw = str(inputs.loanPurpose);
  const rateTerm = /rate\s*\/?\s*term/i.test(purposeRaw);
  out.refinancePurposeID = rateTerm ? optionId(layout, 'RefinancePurposeID', /Rate and term - Conv$/i, UWM.refi.rtConv) : optionId(layout, 'RefinancePurposeID', /^Cash Out$/i, UWM.refi.cashOut);
  if (/purchase/i.test(purposeRaw)) notes.push('A piggyback purchase is sent to UWM as a cash-out HELOC purpose (UWM\'s HELOC form has no purchase purpose).');

  // Terms, lock, product
  const termIds: string[] = [];
  const termLabels = fieldValueList(inputs.term);
  for (const raw of termLabels.length ? termLabels : ['30 Years']) {
    const termYears = Number((raw.match(/(\d+)/) ?? [])[1] ?? 30);
    const termId = UWM.term[termYears];
    if (termId) termIds.push(optionId(layout, 'LoanTermIds', new RegExp(`^${termYears} years`, 'i'), termId));
  }
  out.loanTermIds = termIds.length ? [...new Set(termIds)] : [optionId(layout, 'LoanTermIds', /^30 years/i, UWM.term[30]!)];
  const lockReq = Number(str(inputs.desiredLockPeriod)) || 30;
  const lockDays = [15, 30, 45, 60].find((d) => d >= lockReq) ?? 60;
  if (lockDays !== lockReq) notes.push(`${lockReq}-day lock is not a UWM HELOC lock period — sent as ${lockDays} days.`);
  out.commitmentPeriodID = optionId(layout, 'CommitmentPeriodID', new RegExp(`^${lockDays}-day`, 'i'), UWM.lock[lockDays]!);
  const product = str(inputs.homeEquityProduct);
  const heloan = /heloan|fixed-rate loan/i.test(product) || (!/heloc/i.test(str(inputs.loanType)) && /second/i.test(str(inputs.lienPosition)));
  out.homeEquityTypeId = heloan ? optionId(layout, 'HomeEquityTypeId', /HELOAN/i, '2') : optionId(layout, 'HomeEquityTypeId', /^HELOC/i, '1');
  return { inputs: out, notes };
}

// ---------------------------------------------------------------------------
// Pricing (UWM body keys) → Other Lenders Pricing (catalog values)
// ---------------------------------------------------------------------------
export function uwmInputsToLender(inputs: Record<string, unknown>, layout: UwmLayoutField[] | null): BridgeResult {
  const notes: string[] = [];
  const out: Record<string, string | boolean | string[]> = {};
  const idOf = (v: unknown): string => (Array.isArray(v) ? str(v[0]) : str(v));
  const label = (itemId: string, id: string, fallback: Record<string, string>): string => optionLabel(layout, itemId, id) ?? fallback[id] ?? '';

  // Loan types + amortizations (UWM's LoanTypeIds is a multi-select; each id fuses both).
  const ltIds = Array.isArray(inputs.loanTypeIds) ? inputs.loanTypeIds.map((x) => str(x)).filter(Boolean) : [idOf(inputs.loanTypeIds)].filter(Boolean);
  const loanTypes: string[] = [];
  const amortizations: string[] = [];
  for (const id of ltIds.length ? ltIds : ['0']) {
    const ltLabel = label('LoanTypeIds', id, UWM_LOAN_TYPE_NAMES);
    const t = /fha/i.test(ltLabel) ? 'FHA' : /\bva\b/i.test(ltLabel) ? 'VA' : /usda/i.test(ltLabel) ? 'USDA' : 'Conventional';
    const a = /arm/i.test(ltLabel) ? 'ARM' : 'Fixed';
    if (!loanTypes.includes(t)) loanTypes.push(t);
    if (!amortizations.includes(a)) amortizations.push(a);
  }
  out.loanType = loanTypes;
  out.amortizationType = amortizations;
  const primaryType = loanTypes[0]!;
  out.armType = 'All';
  out.lienPosition = 'First';
  out.productType = 'Standard';

  // Purpose
  const purpose = idOf(inputs.purposeTypeId);
  const purposeLabel = label('PurposeTypeId', purpose, { '1': 'Purchase home', '3': 'Refinance 1st mortgage', '6': 'Construction purchase', '12': 'Construction refinance' });
  if (/purchase/i.test(purposeLabel) || !purposeLabel) out.loanPurpose = 'Purchase';
  else {
    const refi = idOf(inputs.refinancePurposeID);
    const refiLabel = optionLabel(layout, 'RefinancePurposeID', refi) ?? refi;
    if (/cash/i.test(refiLabel) || /cash/i.test(refi)) out.loanPurpose = 'Refi Cash-Out';
    else if (/irrrl|interest rate reduction/i.test(refiLabel + refi)) out.loanPurpose = primaryType === 'VA' ? 'IRRRL' : 'Rate/Term Refinance';
    else if (/streamline/i.test(refiLabel + refi)) out.loanPurpose = primaryType === 'Conventional' ? 'Rate/Term Refinance' : 'Streamline Refinance';
    else out.loanPurpose = primaryType === 'VA' ? 'IRRRL' : 'Rate/Term Refinance';
    if (/construction/i.test(purposeLabel)) notes.push('Construction refinance loaded as a refinance.');
  }
  if (/construction purchase/i.test(purposeLabel)) notes.push('Construction purchase loaded as a purchase.');

  // Amounts
  const loanAmount = money(inputs.loanAmount);
  const appraised = money(inputs.appraisedValue);
  const sales = money(inputs.salesPrice);
  const value = appraised > 0 ? appraised : sales;
  out.baseLoanAmount = loanAmount > 0 ? String(Math.round(loanAmount)) : '';
  out.propertyValue = value > 0 ? String(Math.round(value)) : '';
  out.ltv = loanAmount > 0 && value > 0 ? ((loanAmount / value) * 100).toFixed(3) : '';
  out.subordinateFinancing = money(inputs.secondLoanAmount) > 0;
  out.subordinateAmount = money(inputs.secondLoanAmount) > 0 ? String(Math.round(money(inputs.secondLoanAmount))) : '';

  // Borrower
  const fico = Number(str(inputs.creditScore));
  out.creditScore = Number.isFinite(fico) && fico > 0 ? String(Math.round(fico)) : '';
  const income = money(inputs.monthlyIncome);
  const debt = money(inputs.monthlyDebt);
  if (income > 0) out.monthlyQualifyingIncome = String(Math.round(income));
  if (income > 0 && debt > 0) out.dtiRatio = ((debt / income) * 100).toFixed(1);
  out.firstTimeHomeBuyer = truthy(inputs.firstTimeHomeBuyer);
  out.fthbAmiWaiver = false;
  if (truthy(inputs.firstTimeHomeBuyer)) notes.push('UWM First-time Homebuyer = Yes was carried across; tick "First-time buyer within AMI limits" only if the income qualifies for the LLPA waiver.');

  // Occupancy + property
  const occ = label('OccupancyTypeId', idOf(inputs.occupancyTypeId), { '1': 'Primary Residence', '2': 'Investment property', '3': 'Secondary residence' });
  out.occupancy = /invest/i.test(occ) ? 'Investment Property' : /second/i.test(occ) ? 'Second Home' : 'Primary Residence';
  const pt = idOf(inputs.propertyTypeID ?? inputs.propertyTypeId);
  const ptLabel = label('PropertyTypeID', pt, { '22': 'Single family residence', '25': '2-4 unit dwelling', '30': 'Cooperative', '32': 'Planned unit development', '34': 'Condominium', '38': 'Modular', '48': 'Site condo', '52': 'Manufactured condo single wide', '53': 'Manufactured PUD single wide', '54': 'Manufactured single wide', '55': 'Manufactured multi wide', '56': 'Manufactured condo multi wide', '57': 'Manufactured PUD multi wide' });
  const units = Number(str(inputs.numberOfUnits)) || 1;
  out.legalStructure = /condo/i.test(ptLabel) ? 'Condo' : /pud|planned unit/i.test(ptLabel) ? 'PUD' : 'Single Family';
  out.attachmentType = /site condo/i.test(ptLabel) ? 'Detached' : /condo/i.test(ptLabel) ? 'Attached' : label('AttachmentTypeID', idOf(inputs.attachmentTypeID ?? inputs.attachmentTypeId), { '1': 'Attached', '0': 'Detached' }) === 'Attached' ? 'Attached' : 'Detached';
  out.manufactured = /manufactured/i.test(ptLabel) ? (/single/i.test(ptLabel) ? 'Single-Wide' : 'Double-Wide') : 'No';
  out.numberOfUnits = /2-4 unit/i.test(ptLabel) ? `${Math.min(Math.max(units, 2), 4)} Units` : units >= 2 ? `${Math.min(units, 4)} Units` : '1 Unit';
  if (/cooperative/i.test(ptLabel)) notes.push('Co-op loaded as Single Family — the lender sheets do not price co-ops.');
  if (/modular/i.test(ptLabel)) notes.push('Modular home loaded as Single Family.');

  // Location
  out.propertyZip = str(inputs.propertyZipCode);
  out.state = '';
  out.county = '';

  // Terms (UWM's LoanTermIds is a multi-select too), lock, escrows, buydown, penalty
  const termIdList = Array.isArray(inputs.loanTermIds) ? inputs.loanTermIds.map((x) => str(x)).filter(Boolean) : [idOf(inputs.loanTermIds)].filter(Boolean);
  const terms = [...new Set((termIdList.length ? termIdList : ['4']).map((id) => {
    const termLabel = label('LoanTermIds', id, { '4': '30 years', '3': '25 years', '2': '20 years', '1': '15 years', '0': '10 years' });
    const termYears = Number((termLabel.match(/(\d+)/) ?? [])[1] ?? 30);
    return `${[40, 30, 25, 20, 15, 10].includes(termYears) ? termYears : 30} Years`;
  }))];
  out.term = terms;
  const lockLabel = label('CommitmentPeriodID', idOf(inputs.commitmentPeriodID), { '12': '15-day lock', '13': '30-day lock', '14': '45-day lock', '15': '60-day lock', '16': '90-day lock' });
  const lockDays = Number((lockLabel.match(/(\d+)/) ?? [])[1] ?? 30);
  out.desiredLockPeriod = String([15, 30, 45, 60, 75, 90].find((d) => d >= lockDays) ?? 90);
  if (![15, 30, 45, 60, 75, 90].includes(lockDays)) notes.push(`${lockDays}-day lock loaded as ${out.desiredLockPeriod} days (sheet lock columns).`);
  const escrow = idOf(inputs.escrowWaiverTypeId);
  out.waiveEscrows = escrow && escrow !== '1' ? 'Yes' : 'No';
  // A UWM buydown alias is priced through the lender's LLPA → the lender-paid program here.
  const bd = str(inputs.buyDownAliasID);
  const bdm = bd.replace(/\s/g, '').match(/3-2-1|2-1|1-1|1-0/);
  out.buydown = bdm ? [`${bdm[0].replace(/-/g, '/')} Lender-paid`] : ['None'];
  const ppp = label('PrePaymentPenaltyId', idOf(inputs.prePaymentPenaltyId), { '1': 'None', '7': '1 Year Penalty', '2': '2 Year Penalty', '4': '3 Year Penalty' });
  const pppYears = (ppp.match(/(\d) Year/i) ?? [])[1];
  out.prepaymentPenalty = pppYears ? `${pppYears} Year` : 'None';
  out.vaSubsequentUse = Array.isArray(inputs.useMortgageInsuranceTypeIds) ? inputs.useMortgageInsuranceTypeIds.map(String).includes(UWM.vaUse.subsequent) : false;
  out.vaExemptFundingFee = idOf(inputs.financedMortgageInsuranceTypeId) === UWM.fundingFee.exempt;
  out.desiredPrice = '100.000';
  out.interestOnly = 'No';
  const payer = label('CompensationPayerTypeID', idOf(inputs.compensationPayerTypeID), { '0': 'Borrower paid', '1': 'Lender paid' });
  out.compType = /borrower/i.test(payer) ? 'BP' : 'LP';
  return { inputs: out, notes };
}
